import React, { useCallback, useEffect, useRef, useState } from 'react';
import { taxService } from 'api/services/taxService';

/**
 * GSTIN input checked by the tax engine when the field is left (PMS slice 3a).
 * A valid number shows the state it encodes; the server stores that state, so it
 * is never typed. Used by the property modal and the party masters.
 */
interface Props {
  value: string;
  onChange: (value: string) => void;
  /** Called after each check with the encoded state code, or null when there is none / it is invalid. */
  onChecked?: (placeCode: string | null) => void;
  label?: string;
  error?: string;
  disabled?: boolean;
  emptyHint?: string;
}

type Check = { state: 'idle' | 'checking' | 'valid' | 'invalid' | 'unchecked'; text?: string };

const GstinField: React.FC<Props> = ({ value, onChange, onChecked, label = 'GSTIN', error, disabled, emptyHint }) => {
  const [check, setCheck] = useState<Check>({ state: 'idle' });
  const lastChecked = useRef('');

  const verify = useCallback(async (raw: string) => {
    const gst = raw.trim().toUpperCase();
    if (!gst) { setCheck({ state: 'idle' }); lastChecked.current = ''; onChecked?.(null); return; }
    if (gst === lastChecked.current) return;
    lastChecked.current = gst;
    setCheck({ state: 'checking' });
    try {
      const res = await taxService.parseRegistrationNumber(gst);
      if (!res?.hasFormat) { setCheck({ state: 'unchecked' }); onChecked?.(null); }
      else if (res.isWellFormed) {
        setCheck({ state: 'valid', text: res.placeName ? `${res.placeName} (${res.placeCode})` : res.placeCode ?? '' });
        onChecked?.(res.placeCode ?? null);
      } else {
        setCheck({ state: 'invalid', text: res.problems?.[0]?.message ?? 'Not a valid GSTIN.' });
        onChecked?.(null);
      }
    } catch {
      setCheck({ state: 'unchecked' }); // the server checks again on save
      onChecked?.(null);
    }
  }, [onChecked]);

  // Show the state of a stored GSTIN when the form opens.
  useEffect(() => {
    if (value) void verify(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="form-group" data-field="gstNumber">
      <label className="form-label">{label}</label>
      <input
        type="text"
        className={`form-input${error || check.state === 'invalid' ? ' form-field-error' : ''}`}
        value={value}
        maxLength={15}
        placeholder="e.g., 27AAPFU0939F1ZV"
        onChange={(e) => { onChange(e.target.value.toUpperCase().replace(/\s/g, '')); setCheck({ state: 'idle' }); }}
        onBlur={(e) => { void verify(e.target.value); }}
        disabled={disabled}
        style={{ fontFamily: 'monospace' }}
      />
      {error ? <div className="form-error-message">{error}</div>
        : check.state === 'invalid' ? <div className="form-error-message">{check.text}</div>
        : check.state === 'valid' ? <div style={{ fontSize: '0.75rem', color: 'var(--success, #16a34a)' }}>✓ Valid — {check.text}</div>
        : check.state === 'checking' ? <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Checking…</div>
        : <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{emptyHint ?? 'The state is read from the GSTIN.'}</div>}
    </div>
  );
};

export default GstinField;
